/** Minimal IndexedDB helpers for offline pack + outbox. */

const DB_NAME = "mvk-offline";
const DB_VERSION = 1;

export type OutboxItem = {
  id: string;
  type: "scan" | "engagement";
  eventId: string;
  idempotencyKey: string;
  payload: unknown;
  createdAt: string;
  attempts: number;
  lastError?: string;
  status: "pending" | "failed";
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("packs")) {
        db.createObjectStore("packs", { keyPath: "eventId" });
      }
      if (!db.objectStoreNames.contains("outbox")) {
        const store = db.createObjectStore("outbox", { keyPath: "id" });
        store.createIndex("byStatus", "status", { unique: false });
        store.createIndex("byEvent", "eventId", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("idb open failed"));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("idb tx failed"));
    tx.onabort = () => reject(tx.error ?? new Error("idb tx aborted"));
  });
}

export async function idbGetPack<T>(eventId: string): Promise<T | null> {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction("packs", "readonly");
      const req = tx.objectStore("packs").get(eventId);
      req.onsuccess = () => resolve((req.result as T) ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

export async function idbPutPack(record: { eventId: string } & Record<string, unknown>): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("packs", "readwrite");
  tx.objectStore("packs").put(record);
  await txDone(tx);
}

export async function idbAddOutbox(item: OutboxItem): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("outbox", "readwrite");
  tx.objectStore("outbox").put(item);
  await txDone(tx);
}

export async function idbListPendingOutbox(): Promise<OutboxItem[]> {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction("outbox", "readonly");
      const req = tx.objectStore("outbox").getAll();
      req.onsuccess = () => {
        const all = (req.result as OutboxItem[]) ?? [];
        resolve(all.filter((i) => i.status === "pending" || i.status === "failed"));
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  }
}

export async function idbUpdateOutbox(item: OutboxItem): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("outbox", "readwrite");
  tx.objectStore("outbox").put(item);
  await txDone(tx);
}

export async function idbDeleteOutbox(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("outbox", "readwrite");
  tx.objectStore("outbox").delete(id);
  await txDone(tx);
}
